import React, { useRef, useState } from "react";
import { Camera, Trash2, ImagePlus } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Avatar } from "@/components/layout/AppShell";
import { absUrl } from "@/components/Uploader";
import { AvatarCropper } from "@/components/AvatarCropper";

const MAX_MB = 8;

async function uploadImage(file: File, onProgress: (n: number) => void): Promise<string> {
  if (!file.type.startsWith("image/")) throw new Error("Please choose an image file (JPG, PNG, WebP).");
  if (file.size > MAX_MB * 1024 * 1024) throw new Error(`Image is too large — keep it under ${MAX_MB} MB.`);
  const fd = new FormData();
  fd.append("file", file);
  fd.append("visibility", "public");
  const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" }, onUploadProgress: (p) => onProgress(Math.round((p.loaded / (p.total || p.loaded)) * 100)) });
  return data.url as string;
}

export function ProfilePictureEditor() {
  const { user, refresh } = useAuth();
  const avatarRef = useRef<HTMLInputElement>(null);
  const coverRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState<"avatar" | "cover" | null>(null);
  const [progress, setProgress] = useState(0);
  const [cropFile, setCropFile] = useState<File | null>(null);
  const isReader = !!user?.reader_status;

  const save = async (field: "avatar" | "cover", value: string | null) => {
    await api.patch("/auth/profile", { [field]: value ?? "" });
    await refresh();
  };
  const uploadAndSave = async (field: "avatar" | "cover", f: File) => {
    setBusy(field); setProgress(0);
    try { const url = await uploadImage(f, setProgress); await save(field, url); toast.success(field === "avatar" ? "Profile picture updated" : "Cover updated"); }
    catch (er: any) { toast.error(er?.response ? errMsg(er) : er.message); }
    finally { setBusy(null); }
  };
  const pick = (field: "avatar" | "cover") => async (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (!f.type.startsWith("image/")) return toast.error("Please choose an image file (JPG, PNG, WebP).");
    if (field === "avatar") { setCropFile(f); return; }
    await uploadAndSave(field, f);
  };
  const cropped = async (blob: Blob) => {
    setCropFile(null);
    await uploadAndSave("avatar", new File([blob], "avatar.jpg", { type: "image/jpeg" }));
  };
  const remove = async (field: "avatar" | "cover") => {
    setBusy(field);
    try { await save(field, null); toast.success(field === "avatar" ? "Profile picture removed" : "Cover removed"); } catch (er) { toast.error(errMsg(er)); } finally { setBusy(null); }
  };

  return (
    <div className="card-tactile overflow-hidden" data-testid="profile-picture-section">
      {cropFile && <AvatarCropper file={cropFile} onCancel={() => setCropFile(null)} onDone={cropped} />}
      <input ref={avatarRef} type="file" accept="image/*" className="hidden" onChange={pick("avatar")} data-testid="avatar-input" />
      <input ref={coverRef} type="file" accept="image/*" className="hidden" onChange={pick("cover")} data-testid="cover-input" />
      {isReader && (
        <div className="relative h-28 sm:h-36 bg-[#1A1917]" data-testid="cover-preview">
          {user.cover && <img src={absUrl(user.cover)} alt="" className="h-full w-full object-cover opacity-80" />}
          <div className="absolute inset-0 flex items-end justify-end gap-2 p-3">
            <button type="button" onClick={() => coverRef.current?.click()} disabled={!!busy} className="btn-ghost !py-1.5 !px-3 text-xs !bg-[#F6F4EE]/90" data-testid="cover-change-btn"><ImagePlus size={13} /> {user.cover ? "Change cover" : "Add cover"}</button>
            {user.cover && <button type="button" onClick={() => remove("cover")} disabled={!!busy} className="btn-ghost !py-1.5 !px-3 text-xs !bg-[#F6F4EE]/90" aria-label="Remove cover" data-testid="cover-remove-btn"><Trash2 size={13} /></button>}
          </div>
        </div>
      )}
      <div className={`p-5 sm:p-6 flex flex-wrap items-center gap-5 ${isReader ? "-mt-10 sm:-mt-12" : ""}`}>
        <button type="button" onClick={() => avatarRef.current?.click()} disabled={!!busy} className="relative group rounded-full ring-4 ring-[#F6F4EE] shrink-0" aria-label="Change profile picture" data-testid="avatar-change-btn">
          <Avatar u={user} size={96} />
          <span className="absolute inset-0 rounded-full bg-[#121110]/55 text-[#F6F4EE] flex flex-col items-center justify-center opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100 transition-opacity">
            <Camera size={20} /><span className="mono text-[9px] uppercase mt-1">Change</span>
          </span>
          {busy === "avatar" && <span className="absolute inset-0 rounded-full bg-[#121110]/70 text-[#F6F4EE] flex items-center justify-center mono text-xs" data-testid="avatar-progress">{progress < 100 ? `${progress}%` : "Saving…"}</span>}
        </button>
        <div className={`flex-1 min-w-[200px] ${isReader ? "pt-10 sm:pt-12" : ""}`}>
          <p className="eyebrow">Profile picture</p>
          <p className="text-sm text-[#2C2A28] mt-1">Shown on your posts, readings and messages. You'll be able to crop and zoom before saving · JPG, PNG or WebP up to {MAX_MB} MB.</p>
          <div className="flex flex-wrap gap-2 mt-3">
            <button type="button" onClick={() => avatarRef.current?.click()} disabled={!!busy} className="btn-wine !py-2 text-xs" data-testid="avatar-upload-btn"><Camera size={13} /> {user.avatar ? "Change photo" : "Upload photo"}</button>
            {user.avatar && <button type="button" onClick={() => remove("avatar")} disabled={!!busy} className="btn-ghost !py-2 text-xs" data-testid="avatar-remove-btn"><Trash2 size={13} /> Remove</button>}
          </div>
        </div>
      </div>
    </div>
  );
}
